import Config from '@/config'
import logger from '@/utils/logger'
import Cookies from '@/utils/cookie-provider'
import CookieStorage from '@/utils/cookie-storage'
import { getCheckoutSessionId } from '@/utils/checkout-session'

// This key is used for retrieving shared session credentials from other apps.
// Changing this key could break the functionality, so caution is advised.
const CHECKOUT_SHARED_SESSION_CREDENTIALS_KEY = `${Config.env}-checkout-shared-session-credentials`

// ===== Checkout UI - utils ========
// Keep these functions in Checkout UI
// === Some functions connect to Cookie ===

/**
 * Getter - Checkout UI uses getter only
 * Retrieves the shared session credentials from other Tamara apps.
 *
 * @returns {Object} credentials An object containing the phone number and token.
 * @returns {string} credentials.phone - The phone number associated with the shared session.
 * @returns {string} credentials.token - The token associated with the shared session.
 */
export const getSharedCredentialsByOtherTamaraApps = () => {
  const sharedCredentialsString = Cookies.get(
    CHECKOUT_SHARED_SESSION_CREDENTIALS_KEY
  )

  if (!sharedCredentialsString) {
    return {}
  }

  try {
    const { identifier, token } = JSON.parse(sharedCredentialsString)

    return { identifier, token }
  } catch (error) {
    logger.error(
      error,
      {
        component: 'Utils::auth.js',
        function: 'getSharedCredentialsByOtherTamaraApps',
      },
      { category: 'utils' }
    )

    return {}
  }
}

export const removeSharedCredentialsByOtherTamaraApps = () => {
  return Cookies.remove(
    CHECKOUT_SHARED_SESSION_CREDENTIALS_KEY,
    CookieStorage.getTamaraDomainCookieAttributes(window)
  )
}

/**
 * A private function to validate the token
 * Validates shared session credentials against the current session's phone number.
 *
 * @param {Object} store - The Vuex store instance.
 * @param {Object} router - The vue-router instance.
 * @param {Object} credentials - The shared session credentials.
 * @param {string} credentials.token - The shared session token.
 * @param {string} credentials.phone - The shared session phone number.
 * @returns {boolean} Returns true if the shared session phone number matches the current session's phone number, false otherwise.
 */
const _validateSharedCredentials = (store, router, credentials) => {
  const { token, identifier } = credentials
  const checkoutSessionId = getCheckoutSessionId(store, router)

  if (!token || !identifier || !checkoutSessionId) {
    return false
  }

  return identifier === checkoutSessionId
}

export const initializeSharedCredentialsByOtherTamaraApps = (
  store,
  router,
  routeTo
) => {
  const setValidatingSharedTokenStatus = (status) => {
    store.dispatch(
      'auth/setStatus',
      {
        name: 'validatingSharedToken',
        status,
      },
      { root: true }
    )
  }

  const setCustomerToken = (token) => {
    store.dispatch(
      'auth/setCustomerToken',
      {
        token,
        source: 'sharedToken',
      },
      { root: true }
    )
  }

  const onValidationComplete = () => {
    setValidatingSharedTokenStatus(false)
    removeSharedCredentialsByOtherTamaraApps()
  }

  try {
    setValidatingSharedTokenStatus(true)

    const sharedCredentials = getSharedCredentialsByOtherTamaraApps()
    const isCredentialsValid = _validateSharedCredentials(
      store,
      router,
      sharedCredentials
    )

    if (isCredentialsValid) {
      setCustomerToken(sharedCredentials.token)

      router.replace(
        { name: 'checkout', query: routeTo.query },
        onValidationComplete
      )

      return
    }

    onValidationComplete()
  } catch (error) {
    logger.error(
      error,
      {
        component: 'Utils::auth.js',
        function: 'initializeSharedCredentialsByOtherTamaraApps',
      },
      { category: 'utils' }
    )

    onValidationComplete()
  }
}

// ===== KYC UI - utils ========
// We will move these functions to KYC UI

/**
 * Setter - KYC UI uses setter only
 * Setter function to set shared token to Cookie for checkout UI.
 * It is a part of KYC UI to share the customer token from KYC to Checkout UI
 * @param {Object} credentials
 */
const setSharedCredentialsForCheckoutUi = (credentials) => {
  Cookies.set(
    CHECKOUT_SHARED_SESSION_CREDENTIALS_KEY,
    JSON.stringify(credentials),
    CookieStorage.getTamaraDomainCookieAttributes(window)
  )
}

/**
 *
 * @param {string} identifier
 * @param {string} token
 */
export const shareCustomerTokenToCheckoutUi = (identifier, token) => {
  setSharedCredentialsForCheckoutUi({
    identifier,
    token,
  })
}
